/* ─────────────────────────────────────────────────────────────────────────
   HomeStory — the homepage, told as one engineering build.

   Blueprint → schematic → PCB → printed enclosure → robotic integration →
   power-on → validation → deployment → four industries → capabilities →
   projects → proof → back to the blueprint.

   A fixed WebGL stage (stage/Stage.js) sits behind a tall pinned section.
   Scroll position is turned into `s` (viewport heights through the story)
   and both the 3D and the DOM overlay are pure functions of it. The overlay
   is written straight to the DOM from the same rAF as the render — no React
   state per frame.
   ───────────────────────────────────────────────────────────────────────── */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Stage, { printHold } from './stage/Stage';
import { CH, RAIL, STORY_LEN, envelope, local, chapterAt, clamp, invLerp } from './stage/timeline';
import { printJob } from './stage/enclosure';
import { PRINT_JOB, printRemaining, printLayer } from './stage/printer';
import { HERO, STORY, INDUSTRY_CHAPTERS, CAPABILITIES, PROJECT_ARCHIVE, PROOF, FINAL } from './content';
import Services from '../Services';
import { useReveal } from '../../hooks';
import './HomeStory.css';

/* chapter index, always slashed: 01 → /01 (industries already carry theirs) */
const idx = n => '/' + String(n).replace(/^\//, '');

const isMobile = () => typeof window !== 'undefined' && (window.innerWidth < 900 || window.matchMedia('(pointer: coarse)').matches);

function Arrow({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/* ── Loader ─────────────────────────────────────────────────────────── */
function Loader({ progress, done, failed }) {
  return (
    <div className={`zxh-loader ${done ? 'is-done' : ''}`} aria-hidden={done}>
      <div className="zxh-loader__inner">
        <div className="zxh-loader__mark">ZONE<span>-X</span></div>
        <div className="zxh-loader__bar"><i style={{ transform: `scaleX(${progress})` }} /></div>
        <div className="zxh-loader__meta">
          <span>{failed ? 'LOADING BLUEPRINT' : 'SYSTEM INITIALIZING'}</span>
          <span>{String(Math.round(progress * 100)).padStart(3, '0')}%</span>
        </div>
      </div>
    </div>
  );
}

/* ── Pinned story overlay ───────────────────────────────────────────── */
/* Every chapter is a full-viewport frame, laid out like the storyboard: a pale
   chapter number behind, the copy on the left, the 3D on the right. */
function ChapterPanel({ c, reg, hudReg }) {
  return (
    <div className="zxh-panel" ref={reg(c.id)} data-ch={c.id}>
      <div className="zxh-bignum" aria-hidden="true">{c.n}</div>
      <div className="zxh-copy">
        <div className="zxh-index">{idx(c.n)}</div>
        <h2 className="zxh-title">{c.title}</h2>
        <p className="zxh-body">{c.body}</p>
        {c.id === 'prototype' && <PrintStatus hudReg={hudReg} />}
        {c.tags.length > 0 && <ul className="zxh-tags">{c.tags.map(t => <li key={t}>{t}</li>)}</ul>}
        {c.cta && <Link to={c.cta.to} className="zxh-btn zxh-btn--ghost">{c.cta.label} <Arrow /></Link>}
      </div>
    </div>
  );
}

/* The live job card under the Prototype copy: names what the machine is
   printing — the housing for the board just built — and how far it has got.
   Filled each frame from the stage's print clock (see paint()). */
/* the card's caption line; while the story waits on the printer it says so,
   in the same single line, so the card never changes height */
const PRINT_NOTE = { idle: 'Housing for the PCB just assembled · PLA', hold: 'Story continues once the print is done' };

function PrintStatus({ hudReg }) {
  return (
    <div className="zxh-print" ref={hudReg('print')} aria-live="off">
      <div className="zxh-print__head"><span>Now printing</span><b ref={hudReg('printStat')}>READY</b></div>
      <div className="zxh-print__part">{PRINT_JOB.part}<small ref={hudReg('printNote')}>{PRINT_NOTE.idle}</small></div>
      <div className="zxh-print__bar"><i ref={hudReg('printBar')} /></div>
      <dl>
        <div><dt>Layer</dt><dd ref={hudReg('printLayer')}>0/{PRINT_JOB.layers}</dd></div>
        <div><dt>Progress</dt><dd ref={hudReg('printPct')}>0%</dd></div>
        <div><dt>Remaining</dt><dd ref={hudReg('printEta')}>{printRemaining(0)}</dd></div>
      </dl>
    </div>
  );
}

function IndustryPanel({ c, reg }) {
  return (
    <div className="zxh-panel zxh-panel--ind" ref={reg(c.id)} data-ch={c.id}>
      <div className="zxh-copy">
        <div className="zxh-index">{idx(c.n)}</div>
        <h2 className="zxh-title">{c.name}</h2>
        <p className="zxh-body">{c.desc}</p>
        <ul className="zxh-svc">
          {c.services.map(s => <li key={s.to}><Link to={s.to}><span>{s.title}</span><Arrow size={13} /></Link></li>)}
        </ul>
        <div className="zxh-chips">{c.tags.map(t => <span key={t}>{t}</span>)}</div>
        <Link to={c.to} className="zxh-btn zxh-btn--ghost">Explore {c.short} <Arrow /></Link>
      </div>
    </div>
  );
}

function CapsPanel({ reg, active, setHover }) {
  const it = CAPABILITIES.items[active];
  return (
    <div className="zxh-panel zxh-panel--caps" ref={reg('caps')} data-ch="caps">
      <div className="zxh-bigword" aria-hidden="true">CAPABILITIES</div>
      <div className="zxh-copy">
        <div className="zxh-index">{idx(CAPABILITIES.n)}</div>
        <h2 className="zxh-title">{CAPABILITIES.title}</h2>
        <p className="zxh-body">{CAPABILITIES.body}</p>
        <div className="zxh-capcard" key={it.k}>
          <span>{String(active + 1).padStart(2, '0')} / {String(CAPABILITIES.items.length).padStart(2, '0')}</span>
          <b>{it.k}</b>
          <p>{it.d}</p>
          <Link to={it.to} className="zxh-link">Learn more <Arrow size={13} /></Link>
        </div>
      </div>
      <ol className="zxh-caplist" onMouseLeave={() => setHover(null)}>
        {CAPABILITIES.items.map((x, i) => (
          <li key={x.k} className={active === i ? 'is-on' : ''}>
            <Link to={x.to} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}>
              <i aria-hidden="true">+</i>{x.k}
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ── Sections after the pin ─────────────────────────────────────────── */
function Archive() {
  const [sel, setSel] = useState(0);
  return (
    <section className="zxh-sec zxh-archive" id="projects">
      <div className="zxh-wrap">
        <header className="zxh-sechead">
          <div className="zxh-sechead__n">{PROJECT_ARCHIVE.n}</div>
          <div>
            <div className="zxh-eyebrow"><i />{PROJECT_ARCHIVE.n} — {PROJECT_ARCHIVE.eyebrow}</div>
            <h2 className="zxh-h2">{PROJECT_ARCHIVE.title}</h2>
          </div>
          <Link to="/projects" className="zxh-btn zxh-btn--ghost">View all projects <Arrow /></Link>
        </header>
        <div className="zxh-archive__grid">
          <ol className="zxh-archive__list" role="tablist" aria-label="Projects">
            {PROJECT_ARCHIVE.items.map((it, i) => (
              <li key={it.slug}>
                <button role="tab" aria-selected={i === sel} aria-controls={`zxh-proj-${i}`} className={i === sel ? 'is-on' : ''} onClick={() => setSel(i)} onMouseEnter={() => setSel(i)}>
                  <span className="zxh-archive__code">{it.code}</span>
                  <span className="zxh-archive__title">{it.title}</span>
                  <span className="zxh-archive__sector" style={{ '--c': it.color }}>{it.sector}</span>
                  <span className="zxh-archive__year">{it.year}</span>
                </button>
              </li>
            ))}
          </ol>
          {/* Every card sits in the same grid cell, so the column is always as
              tall as the tallest one. Switching projects never changes the
              layout — otherwise the sticky list shifts under a still cursor and
              re-triggers hover on a neighbouring row. */}
          <div className="zxh-archive__stack">
            {PROJECT_ARCHIVE.items.map((p, i) => (
              <article className={`zxh-archive__card ${i === sel ? 'is-on' : ''}`} key={p.slug} id={`zxh-proj-${i}`} role="tabpanel" aria-hidden={i !== sel}>
                <div className="zxh-archive__media">
                  <img src={p.image} alt={p.title} loading="lazy" />
                  <div className="zxh-archive__stamp"><span>{p.code}</span><span>{p.sector}</span></div>
                </div>
                <div className="zxh-archive__body">
                  <h3>{p.title}</h3>
                  <p className="zxh-archive__tag">{p.tagline}</p>
                  <div className="zxh-per">
                    {[['Problem', p.problem], ['Engineering', p.engineering], ['Result', p.result]].map(([k, v], j) => (
                      <div className="zxh-per__col" key={k}>
                        <div className="zxh-per__k"><span>{String(j + 1).padStart(2, '0')}</span>{k}{j < 2 && <Arrow size={12} />}</div>
                        <p>{v}</p>
                      </div>
                    ))}
                  </div>
                  <div className="zxh-metrics">
                    {p.metrics.map(m => <div key={m.label}><b>{m.value}</b><span>{m.label}</span></div>)}
                  </div>
                  <Link to={`/projects/${p.slug}`} className="zxh-btn">Read the case study <Arrow /></Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Counter({ value, suffix, run }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) return undefined;
    let raf; const t0 = performance.now();
    const step = t => { const k = Math.min(1, (t - t0) / 1600); setV(Math.round(value * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [run, value]);
  return <>{v}{suffix}</>;
}

function Stars({ rating }) {
  return (
    <div className="zxh-stars" aria-label={`Rated ${rating} out of 5`}>
      {[0, 1, 2, 3, 4].map(i => <i key={i} className={rating >= i + 1 ? 'on' : rating > i ? 'half' : ''} />)}<b>{rating}</b>
    </div>
  );
}

/* Client voices — an endless, slow belt of quote cards. The list is rendered
   twice and the track slides by exactly one copy (-50%), so the loop has no
   seam. Hover or focus pauses it; reduced motion turns it into a plain
   horizontal scroller.
   Cards grow into full size as they reach the middle and shrink/fade toward
   the edges. That uses the `scale` property, so the hover lift (`transform`)
   stays independent. */
function Testimonials({ items }) {
  const viewRef = useRef(null);
  const initials = n => n.split(' ').filter(w => /^[A-Z]/.test(w)).map(w => w[0]).join('').slice(0, 2);

  useEffect(() => {
    const view = viewRef.current;
    if (!view || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const cards = [...view.querySelectorAll('.zxh-voice')];
    let raf = 0;
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const v = view.getBoundingClientRect();
      const mid = v.left + v.width / 2, half = v.width / 2;
      for (const el of cards) {
        const r = el.getBoundingClientRect();
        const d = clamp(Math.abs(r.left + r.width / 2 - mid) / half);
        const k = d * d * (3 - 2 * d); // smoothstep: full size holds a little around the centre
        el.style.scale = (1 - 0.16 * k).toFixed(4);
        el.style.opacity = (1 - 0.5 * k).toFixed(3);
      }
    };
    const io = new IntersectionObserver(([e]) => {
      cancelAnimationFrame(raf);
      if (e.isIntersecting) raf = requestAnimationFrame(frame);
    });
    io.observe(view);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [items]);

  return (
    <div className="zxh-voices" aria-label="Client testimonials">
      <div className="zxh-voices__head">
        <div className="zxh-eyebrow"><i />What our clients say</div>
        <span className="zxh-voices__hint" aria-hidden="true">Hover to pause</span>
      </div>
      <div className="zxh-voices__viewport" ref={viewRef}>
        <div className="zxh-voices__track" style={{ '--n': items.length }}>
          {[...items, ...items].map((t, i) => (
            <figure className="zxh-voice" key={i} aria-hidden={i >= items.length}>
              {t.rating ? <Stars rating={t.rating} /> : <div className="zxh-voice__proj" style={{ '--c': t.color }}>{t.sector}</div>}
              <blockquote>“{t.quote}”</blockquote>
              <figcaption>
                <span className="zxh-voice__av">{initials(t.name)}</span>
                <div><b>{t.name}</b><span>{t.role}</span></div>
              </figcaption>
              {t.project && <div className="zxh-voice__src">{t.project}</div>}
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
}

function Proof() {
  const ref = useRef(null);
  const [run, setRun] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setRun(true); io.disconnect(); } }, { threshold: 0.25 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const clients = [...PROOF.clients, ...PROOF.clients, ...PROOF.clients];
  return (
    <section className="zxh-sec zxh-proof" ref={ref} id="about">
      <div className="zxh-wrap">
        <header className="zxh-sechead">
          <div className="zxh-sechead__n">{PROOF.n}</div>
          <div>
            <div className="zxh-eyebrow"><i />{PROOF.n} — {PROOF.eyebrow}</div>
            <h2 className="zxh-h2">{PROOF.title}</h2>
          </div>
          <Link to="/about" className="zxh-btn zxh-btn--ghost">About us <Arrow /></Link>
        </header>
        <div className="zxh-stats">
          {PROOF.stats.map(s => (
            <div key={s.label}><b><Counter value={s.value} suffix={s.suffix} run={run} /></b><span>{s.label}</span></div>
          ))}
        </div>
        <Testimonials items={PROOF.testimonials} />
        <div className="zxh-clients" aria-label="Trusted by">
          <div className="zxh-clients__label">Trusted by</div>
          <div className="zxh-clients__track">{clients.map((c, i) => <span key={i}>{c}</span>)}</div>
        </div>
        {/* "Plug Zone-X into your existing ecosystem" — parked at the client's
            request. Re-enable by uncommenting; the copy is still in content.js.
        <div className="zxh-eco">
          <h3>{PROOF.ecosystemTitle[0]}<em>{PROOF.ecosystemTitle[1]}</em>{PROOF.ecosystemTitle[2]} <span>{PROOF.ecosystemTitle[3]}</span></h3>
          <p>{PROOF.ecosystemSub}</p>
          <div className="zxh-eco__grid">{PROOF.ecosystem.map(t => <span key={t}>{t}</span>)}</div>
        </div> */}
      </div>
    </section>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */
export default function HomeStory() {
  useReveal(); // the Articles block (components/Services.jsx) fades in on .reveal
  const canvasRef = useRef(null);
  const stageWrap = useRef(null);
  const storyRef = useRef(null);
  const ctaRef = useRef(null);
  const panels = useRef({});
  const hud = useRef({});
  const stageRef = useRef(null);
  const hoverRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [capActive, setCapActive] = useState(0);
  const [capHover, setCapHover] = useState(null);
  const capRef = useRef(0);

  const reg = k => el => { if (el) panels.current[k] = el; };
  const hudReg = k => el => { if (el) hud.current[k] = el; };
  const jumpUntil = useRef(0);

  useEffect(() => { hoverRef.current = capHover; }, [capHover]);

  useEffect(() => {
    const mobile = isMobile();
    let stage;
    try { stage = new Stage(canvasRef.current, { mobile }); } catch (e) { setFailed(true); setReady(true); return undefined; }
    stageRef.current = stage;
    if (process.env.NODE_ENV === 'development') window.__zxStage = stage;
    const size = () => stage.resize(window.innerWidth, window.innerHeight);
    size();
    window.addEventListener('resize', size);
    const onPointer = e => { stage.pointer = { x: e.clientX / window.innerWidth - 0.5, y: 0.5 - e.clientY / window.innerHeight }; };
    window.addEventListener('pointermove', onPointer, { passive: true });

    stage.load(p => setProgress(p)).then(() => { setProgress(1); setTimeout(() => setReady(true), 350); })
      .catch(() => { setFailed(true); setReady(true); });

    let raf, last = performance.now();
    const t0 = last;
    let lastS = null;   // last frame's story position, to tell a scroll-through from a jump or reload
    let holding = false;
    const loop = now => {
      // Refs are detached on unmount before this effect's cleanup cancels the
      // frame, so a final tick can land with them null (e.g. navigating away).
      if (!storyRef.current || !ctaRef.current || !stageWrap.current) return;
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      const vh = window.innerHeight;
      const st = storyRef.current.getBoundingClientRect();
      const ct = ctaRef.current.getBoundingClientRect();
      let sRaw = clamp(-st.top / vh, 0, STORY_LEN);
      /* Print hold: scrolling on past the Prototype chapter waits for the
         printer to finish the job rather than skipping the rest of it. The
         story is pinned, so holding the scroll position is invisible — the
         page simply doesn't move on until the last layer is down. Only a
         scroll that arrives from before the hold point is held; a rail jump
         or a reload further down goes straight through. */
      const holdS = printHold();
      const busy = stage.ready && (stage.printK < 1 || stage.parkK < 1);
      /* Sticky: once engaged it stays on until the machine is done (or the
         reader scrolls back up). Re-testing "past the hold point" each frame
         would flip it off on the frame after every snap-back. */
      if (holding && (!busy || sRaw < holdS - 0.005 || now <= jumpUntil.current)) holding = false;
      if (!holding && busy && sRaw > holdS && sRaw < CH.activate[0]
        && lastS !== null && lastS <= holdS + 0.01 && now > jumpUntil.current) holding = true;
      if (holding && sRaw > holdS) {
        window.scrollTo({ top: window.scrollY + st.top + holdS * vh, behavior: 'instant' });
        sRaw = holdS;
      }
      lastS = sRaw;
      if (hud.current.print) hud.current.print.classList.toggle('is-hold', holding);
      const storyVisible = st.bottom > 0;
      const ctaVisible = ct.top < vh && ct.bottom > 0;
      const cta = ctaVisible ? clamp((vh - ct.top) / (ct.height), 0, 1) : -1;
      stage.sTarget = sRaw;
      stage.cta = !storyVisible && ctaVisible ? cta : -1;
      const wrap = stageWrap.current;
      const show = storyVisible || ctaVisible;
      wrap.style.opacity = show ? (storyVisible ? String(clamp(st.bottom / (vh * 0.6))) : String(clamp(cta * 3))) : '0';
      if (show && stage.ready) {
        const hovered = hoverRef.current;
        stage.hover = hovered != null ? CAPABILITIES.items[hovered].layer : null;
        stage.update(dt, (now - t0) / 1000);
      }
      paint(stage, stage.s);
    };
    raf = requestAnimationFrame(loop);

    /* DOM overlay — pure function of s */
    const paint = (stage, s) => {
      const P = panels.current, H = hud.current;
      const vis = (el, o, y = 18) => {
        if (!el) return;
        el.style.opacity = o.toFixed(3);
        el.style.transform = `translate3d(0, ${((1 - o) * y).toFixed(1)}px, 0)`;
        el.style.visibility = o < 0.05 ? 'hidden' : 'visible';
      };
      vis(P.hero, 1 - clamp(invLerp(0.45, 0.95, s)), -30);
      STORY.forEach(c => vis(P[c.id], envelope(s, CH[c.id][0], CH[c.id][1], 0.3, 0.3)));
      INDUSTRY_CHAPTERS.forEach(c => vis(P[c.id], envelope(s, CH[c.id][0], CH[c.id][1], 0.3, 0.3)));
      vis(P.caps, envelope(s, CH.caps[0], CH.caps[1] + 2, 0.25, 0.05));

      const k = chapterAt(s);
      const idx = RAIL.findIndex(r => r[0] === k);
      if (H.rail) H.rail.querySelectorAll('li').forEach((li, i) => { li.classList.toggle('is-on', i === idx); li.classList.toggle('is-done', i < idx); });
      if (H.bar) H.bar.style.transform = `scaleY(${(s / STORY_LEN).toFixed(4)})`;
      if (H.hudWrap) H.hudWrap.style.opacity = String(1 - clamp(invLerp(STORY_LEN - 0.2, STORY_LEN + 0.6, s)));

      /* pinned labels */
      const L = stage.labels || {};
      const pin = (el, p, o) => {
        if (!el) return;
        if (!p || p.z > 1) { el.style.opacity = '0'; return; }
        el.style.opacity = o.toFixed(3);
        el.style.transform = `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0)`;
      };
      const dimO = (1 - clamp(invLerp(CH.build[0] + 0.2, CH.build[0] + 0.9, s))) * clamp(stage.intro * 2 - 0.6);
      pin(H.dimW, L.dimW, dimO); pin(H.dimH, L.dimH, dimO);
      pin(H.fpga, L.fpga, envelope(s, CH.idea[0] + 0.5, CH.build[1] - 0.2, 0.3, 0.3));
      const valO = envelope(s, CH.validate[0] + 0.1, CH.validate[1] - 0.05, 0.25, 0.2);
      pin(H.val, L.enc, valO);
      if (H.val && valO > 0) {
        const pass = local(s, 'validate') >= 0.72;
        H.val.classList.toggle('is-pass', pass);
        const t = performance.now() / 1000;
        H.valT.textContent = (36.4 + Math.sin(t * 1.3) * 0.1).toFixed(1) + '°C';
        H.valS.textContent = Math.round(97.6 + Math.sin(t * 0.9) * 0.6) + '%';
        H.valP.textContent = (12.2 + Math.sin(t * 2.1) * 0.08).toFixed(1) + 'W';
        H.valStat.textContent = pass ? 'PASS' : 'TESTING';
      }
      /* the print job card follows what the machine has actually laid, not the scroll */
      if (H.print && s >= CH.prototype[0] - 0.3 && s < CH.integrate[0] + 0.3) {
        const p = stage.printK || 0, done = p >= 1;
        const job = printJob(p);
        const what = job.floor ? 'Base' : 'Walls';
        H.print.classList.toggle('is-done', done);
        const hold = H.print.classList.contains('is-hold');
        H.printStat.textContent = p <= 0 ? 'READY' : done ? 'COMPLETE' : hold ? `FINISHING · ${what}` : `PRINTING · ${what}`;
        H.printNote.textContent = hold ? PRINT_NOTE.hold : PRINT_NOTE.idle;
        H.printBar.style.transform = `scaleX(${p.toFixed(4)})`;
        H.printLayer.textContent = `${printLayer(p)}/${PRINT_JOB.layers}`;
        H.printPct.textContent = `${Math.floor(p * 100)}%`;
        H.printEta.textContent = done ? 'Done' : printRemaining(p);
      }
      const nodeO = envelope(s, CH.deploy[0] + 0.95, CH.deploy[1] + 0.15, 0.2, 0.25);
      (L.nodes || []).forEach((p, i) => pin(H[`node${i}`], p, nodeO));

      /* capabilities auto-cycle with scroll (hover overrides) */
      if (s >= CH.caps[0]) {
        const a = Math.min(CAPABILITIES.items.length - 1, Math.floor(invLerp(0.35, 0.98, local(s, 'caps')) * CAPABILITIES.items.length));
        stage.autoLayer = CAPABILITIES.items[a].layer;
        if (a !== capRef.current) { capRef.current = a; setCapActive(a); }
      }
    };

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
      window.removeEventListener('pointermove', onPointer);
      stage.dispose();
    };
  }, []);

  const jump = k => {
    const top = storyRef.current.getBoundingClientRect().top + window.scrollY;
    const [a, b] = CH[k];
    jumpUntil.current = performance.now() + 1800;   // a chosen jump isn't held by the print
    window.scrollTo({ top: top + (a + (b - a) * 0.45) * window.innerHeight, behavior: 'smooth' });
  };

  const active = capHover != null ? capHover : capActive;
  const vh = STORY_LEN + 1;

  return (
    <div className="zxh-home">
      <Loader progress={progress} done={ready} failed={failed} />
      <div className="zxh-stage" ref={stageWrap} aria-hidden="true">
        <canvas ref={canvasRef} />
        {failed && <div className="zxh-stage__fallback" style={{ backgroundImage: 'url("/visuals/First page animation 1.png")' }} />}
      </div>

      {/* ── the pinned story ── */}
      <section className="zxh-story" ref={storyRef} style={{ height: `${vh * 100}vh` }} aria-label="How Zone-X engineers a product">
        <div className="zxh-sticky">
          <div className="zxh-hero" ref={reg('hero')}>
            <div className="zxh-hero__copy">
              <div className="zxh-hero__tag">{HERO.tagline.map((t, i) => <React.Fragment key={t}>{i > 0 && <i>|</i>}<span>{t}</span></React.Fragment>)}</div>
              <h1 className="zxh-hero__h1">
                <span className="zxh-hero__l1">{HERO.line1[0]}<em>{HERO.line1[1]}</em>{HERO.line1[2]}</span>
                <span className="zxh-hero__l2">{HERO.line2}</span>
              </h1>
              <p className="zxh-hero__sub">{HERO.sub[0]}<em>{HERO.sub[1]}</em></p>
              <div className="zxh-hero__actions">
                <Link to={HERO.cta.to} className="zxh-btn">{HERO.cta.label} <Arrow /></Link>
                <button type="button" className="zxh-btn zxh-btn--ghost" onClick={() => jump('idea')}>See how we build <Arrow /></button>
              </div>
              <ol className="zxh-steps">
                {HERO.steps.map((t, i) => <li key={t}><span>{String(i + 1).padStart(2, '0')}</span>{t}</li>)}
              </ol>
            </div>
            <div className="zxh-hero__scroll"><span>SYSTEM INITIALIZING</span><i /><span>SCROLL</span></div>
          </div>

          {STORY.map(c => <ChapterPanel key={c.id} c={c} reg={reg} hudReg={hudReg} />)}
          {INDUSTRY_CHAPTERS.map(c => <IndustryPanel key={c.id} c={c} reg={reg} />)}
          <CapsPanel reg={reg} active={active} setHover={setCapHover} />

          {/* pinned labels */}
          <div className="zxh-pin zxh-pin--dim" ref={hudReg('dimW')}><span>83.2 mm</span></div>
          <div className="zxh-pin zxh-pin--dim zxh-pin--v" ref={hudReg('dimH')}><span>53.0 mm</span></div>
          <div className="zxh-pin zxh-pin--call" ref={hudReg('fpga')}><i /><span>U1 · FPGA</span></div>
          <div className="zxh-pin zxh-val" ref={hudReg('val')}>
            <div className="zxh-val__box">
            <div className="zxh-val__head"><span>SYSTEM TEST</span><b ref={hudReg('valStat')}>TESTING</b></div>
            <dl>
              <div><dt>Temperature</dt><dd ref={hudReg('valT')}>36.4°C</dd></div>
              <div><dt>Signal</dt><dd ref={hudReg('valS')}>98%</dd></div>
              <div><dt>Power</dt><dd ref={hudReg('valP')}>12.2W</dd></div>
            </dl>
            <div className="zxh-val__pass">✓ VALIDATED</div>
            </div>
          </div>
          {INDUSTRY_CHAPTERS.map((c, i) => (
            <div key={c.id} className="zxh-pin zxh-node" ref={hudReg(`node${i}`)}>
              <div className="zxh-node__card"><b>{c.short}</b><span>{c.tagline}</span></div><i />
            </div>
          ))}

          {/* HUD */}
          <div className="zxh-hud" ref={hudReg('hudWrap')}>
            <ol className="zxh-rail" ref={hudReg('rail')} aria-label="Chapters">
              {RAIL.map(([k, label]) => (
                <li key={k}><button type="button" onClick={() => jump(k)}><span>{label}</span><i /></button></li>
              ))}
            </ol>
            <div className="zxh-progress" aria-hidden="true"><i ref={hudReg('bar')} /></div>
          </div>
        </div>
      </section>

      <Archive />
      <Proof />
      <div className="zxh-articles"><Services /></div>

      {/* ── final CTA: back to the blueprint ── */}
      <section className="zxh-cta" ref={ctaRef}>
        <div className="zxh-cta__sticky">
          <div className="zxh-cta__word" aria-hidden="true">NEXT</div>
          <div className="zxh-cta__copy">
            <div className="zxh-panel__n">{FINAL.n}</div>
            <div className="zxh-eyebrow"><i />{FINAL.n} — {FINAL.eyebrow}</div>
            <h2 className="zxh-cta__h">{FINAL.title}<br /><em>{FINAL.accent}</em></h2>
            <div className="zxh-hero__actions">
              <Link to={FINAL.cta.to} className="zxh-btn">{FINAL.cta.label} <Arrow /></Link>
              <Link to={HERO.cta.to} className="zxh-btn zxh-btn--ghost">{HERO.cta.label} <Arrow /></Link>
            </div>
          </div>
          <ol className="zxh-cta__trail">{FINAL.trail.map(t => <li key={t}>{t}</li>)}</ol>
        </div>
      </section>
    </div>
  );
}
